import { useState } from 'react';
import {
  Zap,
  Mail,
  MessageSquare,
  Megaphone,
  Clock,
  Bell,
  GitBranch,
  Star,
  Tag,
  LogOut,
  PenLine,
  Search,
} from 'lucide-react';

type JourneyNodeType =
  | 'trigger'
  | 'send_email'
  | 'send_sms'
  | 'send_campaign'
  | 'wait_duration'
  | 'wait_event'
  | 'condition'
  | 'add_points'
  | 'remove_points'
  | 'issue_coupon'
  | 'update_attribute'
  | 'exit';

interface PaletteItem {
  type: JourneyNodeType;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string; // tailwind border color
  iconColor: string;
  dotColor: string; // tailwind bg color for the dot accent
}

const PALETTE_ITEMS: PaletteItem[] = [
  {
    type: 'trigger',
    label: 'Trigger',
    description: 'Start of automation',
    icon: Zap,
    color: 'border-emerald-500',
    iconColor: 'text-emerald-500',
    dotColor: 'bg-emerald-500',
  },
  {
    type: 'send_email',
    label: 'Send Email',
    description: 'Send an email template',
    icon: Mail,
    color: 'border-indigo-500',
    iconColor: 'text-indigo-500',
    dotColor: 'bg-indigo-500',
  },
  {
    type: 'send_sms',
    label: 'Send SMS',
    description: 'Send an SMS template',
    icon: MessageSquare,
    color: 'border-violet-500',
    iconColor: 'text-violet-500',
    dotColor: 'bg-violet-500',
  },
  {
    type: 'send_campaign',
    label: 'Send Campaign',
    description: 'Send to a segment',
    icon: Megaphone,
    color: 'border-cyan-500',
    iconColor: 'text-cyan-500',
    dotColor: 'bg-cyan-500',
  },
  {
    type: 'wait_duration',
    label: 'Wait',
    description: 'Delay for a duration',
    icon: Clock,
    color: 'border-amber-500',
    iconColor: 'text-amber-500',
    dotColor: 'bg-amber-500',
  },
  {
    type: 'wait_event',
    label: 'Wait Event',
    description: 'Wait for an event',
    icon: Bell,
    color: 'border-amber-400',
    iconColor: 'text-amber-400',
    dotColor: 'bg-amber-400',
  },
  {
    type: 'condition',
    label: 'Condition',
    description: 'Branch on a condition',
    icon: GitBranch,
    color: 'border-zinc-500',
    iconColor: 'text-zinc-400',
    dotColor: 'bg-zinc-500',
  },
  {
    type: 'add_points',
    label: 'Add Points',
    description: 'Award loyalty points',
    icon: Star,
    color: 'border-emerald-400',
    iconColor: 'text-emerald-400',
    dotColor: 'bg-emerald-400',
  },
  {
    type: 'remove_points',
    label: 'Remove Points',
    description: 'Deduct loyalty points',
    icon: Star,
    color: 'border-emerald-400',
    iconColor: 'text-emerald-400',
    dotColor: 'bg-emerald-400',
  },
  {
    type: 'issue_coupon',
    label: 'Issue Coupon',
    description: 'Issue a discount coupon',
    icon: Tag,
    color: 'border-orange-500',
    iconColor: 'text-orange-500',
    dotColor: 'bg-orange-500',
  },
  {
    type: 'update_attribute',
    label: 'Update Attribute',
    description: 'Set a customer attribute',
    icon: PenLine,
    color: 'border-sky-500',
    iconColor: 'text-sky-500',
    dotColor: 'bg-sky-500',
  },
  {
    type: 'exit',
    label: 'Exit',
    description: 'End the automation',
    icon: LogOut,
    color: 'border-red-500',
    iconColor: 'text-red-500',
    dotColor: 'bg-red-500',
  },
];

const PALETTE_GROUPS = [
  { label: 'Trigger', types: ['trigger'] },
  { label: 'Messaggi', types: ['send_email', 'send_sms', 'send_campaign'] },
  { label: 'Attesa', types: ['wait_duration', 'wait_event'] },
  { label: 'Logica', types: ['condition'] },
  { label: 'Loyalty', types: ['add_points', 'remove_points', 'issue_coupon'] },
  { label: 'Profilo', types: ['update_attribute'] },
  { label: 'Sistema', types: ['exit'] },
];

// Build a lookup map for quick access
const ITEMS_BY_TYPE = new Map<string, PaletteItem>(PALETTE_ITEMS.map((i) => [i.type, i]));

export default function NodePalette() {
  const [searchQuery, setSearchQuery] = useState('');

  function onDragStart(event: React.DragEvent, nodeType: JourneyNodeType) {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  }

  const query = searchQuery.trim().toLowerCase();

  // When searching, flatten all items and filter by label
  const filteredItems = query
    ? PALETTE_ITEMS.filter((item) => item.label.toLowerCase().includes(query))
    : null;

  return (
    <div className="w-56 border-r border-zinc-700 bg-zinc-900 flex flex-col shrink-0 overflow-hidden">
      {/* Search */}
      <div className="px-3 py-2.5 border-b border-zinc-700">
        <div className="relative">
          <Search
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Cerca nodo…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-zinc-800 text-zinc-200 placeholder-zinc-500 text-xs rounded-md pl-6 pr-2 py-1.5 border border-zinc-700 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
          />
        </div>
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto py-2">
        {filteredItems ? (
          /* Flat filtered list — no group headers */
          filteredItems.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-zinc-500">Nessun nodo trovato</p>
          ) : (
            <div className="flex flex-col gap-0.5 px-2">
              {filteredItems.map((item) => (
                <DraggableRow key={item.type} item={item} onDragStart={onDragStart} />
              ))}
            </div>
          )
        ) : (
          /* Grouped list */
          PALETTE_GROUPS.map((group) => {
            const groupItems = group.types
              .map((t) => ITEMS_BY_TYPE.get(t))
              .filter((i): i is PaletteItem => !!i);
            if (groupItems.length === 0) return null;
            return (
              <div key={group.label} className="mb-3">
                <p className="px-3 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  {group.label}
                </p>
                <div className="flex flex-col gap-0.5 px-2">
                  {groupItems.map((item) => (
                    <DraggableRow key={item.type} item={item} onDragStart={onDragStart} />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function DraggableRow({
  item,
  onDragStart,
}: {
  item: PaletteItem;
  onDragStart: (e: React.DragEvent, type: JourneyNodeType) => void;
}) {
  const Icon = item.icon;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, item.type)}
      title={item.description}
      className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-grab active:cursor-grabbing hover:bg-zinc-800 transition-colors group select-none"
    >
      {/* Color dot */}
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${item.dotColor}`} />
      {/* Icon */}
      <Icon size={13} className={`${item.iconColor} shrink-0`} />
      {/* Label */}
      <span className="text-xs text-zinc-300 group-hover:text-zinc-100 transition-colors truncate">
        {item.label}
      </span>
    </div>
  );
}
